<template lang="pug">
div
    div(class="d-flex flex-column main-container p-2" v-if="!loading && !error && fowEligible === false")
        div(class="feature-banner")
            b-icon(icon="info-circle" class="mr-2")
            div
                strong FOW clients only
                p.mb-0 MC submissions are available for clients attending Family of Wisdom (FOW) sessions. This client currently has no active FOW schedule.
        b-button(variant="outline-primary" size="sm" class="align-self-start" @click="$router.replace('/home')") Back to Home

    div(class="d-flex flex-column main-container p-2" v-else-if="!loading && !error")
        //- For FOW clients only; proof document is mandatory
        div(class="feature-banner feature-banner--mc" data-tour="intro")
            b-icon(icon="file-earmark-medical" class="mr-2")
            div(class="flex-grow-1")
                strong Submit Leave
                p.mb-0 Submit MC/HL/Doctor Appointment for missed FOW sessions. #[strong Proof is required] — attach a photo or PDF of the document before submitting.

            b-button(size="sm" variant="outline-dark" class="howto-btn ml-2 flex-shrink-0" @click="$refs.tour.start()" title="Show me how")
                b-icon(icon="question-circle" class="mr-1")
                | How to

        how-to-tour(ref="tour" :steps="tourSteps" storage-key="howto_mc_v1" auto-start)

        b-tabs.card-pills.card-accent-tabs(class="w-100" ref="mainTabs" v-model="activeTab")
            b-tab(title="Submit MC" :title-link-class="'text-warning'")
                SubmitMCTab(:userId="userId" @request-submitted="handleRequestSubmitted" ref="mcTab")

            b-tab(title="My MC Requests" :title-link-class="'text-info'" :title-link-attributes="{ 'data-tour': 'my-requests' }")
                MyRequestsTab(:userId="userId" type-filter="mc" ref="myRequestsTab")

    div(v-if="loading" class="d-flex w-100 h-100 flex-column align-items-center p-2")
        div(class="tabs-skeleton w-100 d-flex" style="height: 50px;gap:5px;" )
            b-skeleton(height="5vh" width="30%" class="my-2" v-for="i in 2" :key="i")
        b-skeleton(height="30vh" width="100%" class="my-2")
        b-skeleton(height="30vh" width="100%" class="my-2")

    div(v-if="error" class="d-flex w-100 h-100 flex-column align-items-center justify-content-center p-2")
        lottie-animation(:animation-data="animationData" :autoPlay="true" :loop="true" :speed="1" ref="anim"  class="w-50")
        p(class="text-danger text-center") Its not you, its us. Something went wrong. Please try again later.
        b-button(@click="initialize" variant="primary" class="mt-2") Try Again
</template>

<script>
import errorAnimation from "@/assets/errorAnimation.json";
import LottieAnimation from 'lottie-web-vue';
import SubmitMCTab from '@/components/makeup/SubmitMCTab.vue';
import MyRequestsTab from '@/components/makeup/MyRequestsTab.vue';
import HowToTour from '@/components/makeup/HowToTour.vue';
import fowEligibility from '@/mixins/fowEligibility';

export default {
    mixins: [fowEligibility],
    components: {
        LottieAnimation,
        SubmitMCTab,
        MyRequestsTab,
        HowToTour
    },
    data() {
        return {
            animationData: errorAnimation,
            loading: true,
            error: false,
            userId: null,
            activeTab: 0,
            tourSteps: [
                { title: 'Submit MC', icon: 'file-earmark-medical', target: 'intro', text: 'Use this page when a medical certificate covers FOW sessions you cannot attend. Tap <b>How to</b> any time to see these steps again.' },
                { title: '1 · Choose the MC dates', icon: 'calendar3', target: 'mc-dates', text: 'Pick a <b>date range</b> for a continuous MC, or <b>specific days</b> if only some sessions are affected.' },
                { title: '2 · Write the reason', icon: 'chat-left-text', target: 'mc-reason', text: 'A short reason (at least 10 characters), e.g. "Hospitalised for 3 days".' },
                { title: '3 · Attach the proof', icon: 'paperclip', target: 'mc-proof', text: 'A <b>photo or PDF of the MC is required</b> — up to 5 files, 4 MB in total. Tap the upload box to take a photo or choose a file.' },
                { title: '4 · Submit', icon: 'send', target: 'mc-submit', text: 'Tap <b>Submit Request</b>. We will ask for your email the first time so we can update you on the outcome.' },
                { title: '5 · Track it', icon: 'list-check', target: 'my-requests', text: 'Follow the approval under <b>My MC Requests</b>. Billed dates are refunded by credit note (6–8 weeks); unbilled dates are simply left off the next invoice.' },
            ],
        };
    },
    async mounted() {
        await this.initialize();
    },
    methods: {
        async initialize() {
            this.loading = true;
            this.error = false;

            try {
                await this.loadCurrentUser();
                this.userId = this.$root.currentUser[0].crb5c_client.crb5c_fow_customerid;
                if (!this.userId) {
                    throw new Error('User ID not found');
                }
                await this.ensureFowEligibility();
                this.loading = false;
            } catch (err) {
                console.error('Error initializing submit MC page:', err);
                this.error = true;
                this.loading = false;
            }
        },
        async loadCurrentUser() {
            while (!this.$root.currentUser[0]) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
        },
        handleRequestSubmitted() {
            // Jump to the "My ..." tab so the new request is visible right away
            // (the success modal renders over it).
            this.activeTab = 1;
            if (this.$refs.myRequestsTab) {
                this.$refs.myRequestsTab.loadRequests();
            }
            if (this.$refs.mcTab) {
                this.$refs.mcTab.loadAvailableDates();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.main-container {
    min-height: 100vh;
    background-color: #f5f5f5;
}

.howto-btn {
    font-size: 0.75rem;
    font-weight: 700;
    white-space: nowrap;
    border-radius: 999px;
    padding: 0.2rem 0.6rem;
    align-self: flex-start;
}

.feature-banner {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    background: #fff8e8;
    border-left: 4px solid #f0ad4e;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-bottom: 0.75rem;
    font-size: 0.85rem;
    text-align: left;
}
</style>
